// Shared "Schedule" conversion firing (browser pixel + server CAPI on one
// shared event id). Guarded so a booking is only counted once per session,
// whether it was detected via the calendar's postMessage or via the
// redirect to /thank-you that the calendar performs after booking.

import { fireScheduleEvent, newScheduleEventID, readLeadEvent } from "./meta-pixel";
import { trackScheduleConversion } from "./meta-capi.functions";
import { getAttribution } from "./attribution";

const GUARD_KEY = "meta_schedule_fired_v1";

export function scheduleAlreadyFired(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return sessionStorage.getItem(GUARD_KEY) === "1";
  } catch {
    return false;
  }
}

function markFired() {
  try {
    sessionStorage.setItem(GUARD_KEY, "1");
  } catch {
    /* ignore */
  }
}

export type ScheduleLead = {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  tier?: string | null;
  outcome?: string | null;
};

/**
 * Fires the Schedule event once — but ONLY for the Demo Call calendar
 * (Tier 3 / qualified leads). Intro (Discovery) Call bookings never fire a
 * Schedule conversion. Returns true when it actually fired.
 */
export type ScheduleResult = {
  fired: boolean;
  /** Why nothing was sent, when fired === false. */
  reason: string | null;
  eventId: string | null;
  tier: string | null;
};

export function fireScheduleConversion(lead: ScheduleLead = {}): ScheduleResult {
  if (typeof window === "undefined") return { fired: false, reason: "ssr", eventId: null, tier: null };
  const stored = readLeadEvent();
  const tier = lead.tier ?? stored?.tier ?? null;
  if (scheduleAlreadyFired()) return { fired: false, reason: "already_fired_this_session", eventId: null, tier };
  // Demo calendar only — intro (discovery) bookings never send a Schedule.
  if (tier !== "3") return { fired: false, reason: `intro_calendar_tier_${tier ?? "unknown"}`, eventId: null, tier };
  markFired();

  const eventID = newScheduleEventID();
  fireScheduleEvent(eventID, stored);

  const attribution = getAttribution();
  void trackScheduleConversion({
    data: {
      event_id: eventID,
      email: lead.email ?? stored?.email ?? null,
      phone: lead.phone ?? stored?.phone ?? null,
      first_name: lead.firstName ?? stored?.firstName ?? null,
      last_name: lead.lastName ?? stored?.lastName ?? null,
      fbp: attribution.fbp ?? null,
      fbc: attribution.fbc ?? null,
      page_url: window.location.href,
      tier: lead.tier ?? null,
      outcome: lead.outcome ?? null,
    },
  }).catch((err) => console.error("[Meta CAPI] Schedule failed", err));

  return { fired: true, reason: null, eventId: eventID, tier };
}
